import {z as zod} from '@providers/zod';

export interface Indicator {
  uuid?: string;
  label: string;
  baseIndicatorUuid: string;
  description: string;
  defaultDashboard?: string;
  aide?: string;
  modifiable?: boolean;
  order: number;
  config: IndicatorConfig;
  modifiedAt: string;
  active: boolean;
}

export interface IndicatorTissotParameter {
  x: string;
  y: string;
  z: string;
  totalX: boolean;
  totalY: boolean;
  totalZ: boolean;
  cutTables: boolean;
  donnee: string;
  methode: string;
  filters: Filters;
  chartType: string;
  possibleX: string[];
  possibleDonnees: string[];
  possibleChartTypes: string[];
  possibleFilters: string[];
}

export interface IndicatorTissotFilters {
  gender: string[];
  contractNature: string[];
  time_modality: string[];
  nationality: string[];
  change: string[];
  csp: string[];
  tranche_age: string[];
  anciennete: string[];
  motif_fin: string[];
  motif_arret: string[];
  hired: string[];
  disabled_worker: string[];
  promoted: string[];
  has_activite: string[];
  present_in_month: string[];
  present_end_of_month: string[];
  present_all_month: string[];
  last_contract: string[];
  leaving: string[];
}

export const IndicatorTissotFiltersEnum = [
  'gender',
  'contractNature',
  'time_modality',
  'nationality',
  'change',
  'csp',
  'tranche_age',
  'anciennete',
  'motif_fin',
  'motif_arret',
  'hired',
  'disabled_worker',
  'promoted',
  'has_activite',
  'present_in_month',
  'present_end_of_month',
  'present_all_month',
  'last_contract',
  'leaving',
];

export interface IndicatorConfig {
  chartType: string;
  cutTables: boolean;
  donnee: string;
  methode: string;
  fixed: boolean;
  totalX: boolean;
  totalY: boolean;
  totalZ: boolean;
  filters: Filters;
  x: string;
  y: string;
  z: string;
  possibleX: string[];
  possibleDonnees: string[];
  possibleChartTypes: string[];
}

export const IndicatorSchema = zod
  .object({
    label: zod.string().min(1, 'Label is required'),
    defaultDashboard: zod.string().nullable().optional(),
    active: zod.boolean(),
    description: zod.string().optional(),
    aide: zod.string().optional(),
    fixedIndicatorId: zod.number(),
    config: zod.object({
      x: zod.string(),
      y: zod.string(),
      z: zod.string(),
      totalX: zod.boolean(),
      totalY: zod.boolean(),
      totalZ: zod.boolean(),
      cutTables: zod.boolean(),
      donnee: zod.string(),
      methode: zod.string(),
      filters: zod.record(zod.array(zod.string())),
      chartType: zod.string(),
      possibleX: zod.array(zod.string()),
      possibleDonnees: zod.array(zod.string()),
      possibleChartTypes: zod.array(zod.string()),
      possibleFilters: zod.array(zod.string()).optional(),
    }),
  })
  .superRefine((data, ctx) => {
    if (
      data.active &&
      [data.label, data.config.chartType].includes('') &&
      data.fixedIndicatorId === 0 &&
      [data.config.x, data.config.y, data.config.methode].includes('')
    ) {
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        fatal: true,
        path: ['active'],
        message: 'Les indicateurs actifs doivent avoir toutes les données renseignées',
      });
    }
  });

export const IndicatorDimensionsValues: Array<{label: string; value: string}> = [
  {label: 'Aucune', value: 'none'},
  {label: 'Mois', value: 'month'},
  {label: 'Année', value: 'year'},
  {label: 'Sexe', value: 'gender'},
  {label: 'Nature de contrat', value: 'contract_nature'},
  {label: 'Catégorie socio-professionnelle', value: 'csp'},
  {label: 'Positionnement dans la CCN', value: 'convention_position'},
  {label: 'Libellé emploi', value: 'job_label'},
  {label: 'Modalité de temps de travail', value: 'time_modality'},
  {label: 'Nationalité', value: 'nationality'},
  {label: 'Siren', value: 'siren'},
  {label: 'Siret', value: 'siret'},
  {label: 'Changement dans la période', value: 'change'},
  {label: "Taux d'activité", value: 'activity_rate'},
  {label: "Tranche d'âge", value: 'tranche_age'},
  {label: "Tranche d'âge fine", value: 'tranche_age_fine'},
  {label: 'Âge', value: 'age'},
  {label: 'Ancienneté', value: 'anciennete'},
];

export const IndicatorDonneeValues: Array<{label: string; value: string}> = [
  {label: 'Compter les individus uniques', value: ''},
  {label: 'Équivalent temps plein', value: 'etp'},
  {label: 'Âge', value: 'old'},
  {label: 'Âge au 31/12', value: 'end_year_age'},
  {label: "Années d'ancienneté", value: 'seniority'},
  {label: 'Quotité', value: 'quotient'},
  {label: 'Taux d’activité du contrat', value: 'contract_activity'},
  {label: 'Montant de la participation', value: 'share_amount'},
  {label: 'Montant d’intéressement', value: 'interest_amount'},
  {label: 'Montant des primes', value: 'bonuses_amount'},
  {label: 'Nombre de jours d’absence pour maladie', value: 'sick_days'},
  {label: 'Nombre de jours d’absence pour maladie professionnelle', value: 'pro_sick_days'},
  {label: 'Nombre de jours d’absence pour maladie non professionnelle', value: 'no_pro_sick_days'},
  {label: 'Nombre d’arrets pour accident du travail', value: 'pro_accident_stop'},
  {label: 'Nombre d’absences pour maladie professionnelle', value: 'pro_sick_stop'},
  {label: 'Nombre d’absences pour maladie non professionnelle', value: 'no_pro_sick_stop'},
  {label: 'Nombre de sorties', value: 'nb_sortie'},
  {label: 'Nombre d’embauches', value: 'nb_embauche'},
  {label: 'Rémunération brute non plafonnée', value: 'remuneration_brute'},
  {label: 'Salaire de base ETP', value: 'salaire_base_etp'},
];

export const IndicateurMethodeValues: Array<{label: string; value: string}> = [
  {label: 'Somme', value: 'sum'},
  {label: 'Moyenne', value: 'avg'},
  {label: 'Minimum', value: 'min'},
  {label: 'Maximum', value: 'max'},
  {label: 'Compter', value: 'count'},
];

export const IndicatorTissotFiltersValues = {
  gender: [
    {label: 'Homme', value: 'Homme'},
    {label: 'Femme', value: 'Femme'},
  ],
  contractNature: [
    {label: 'CDI', value: 'CDI'},
    {label: 'CDD', value: 'CDD'},
    {label: 'CTT', value: 'CTT'},
    {label: 'Apprenti', value: 'Apprenti'},
    {label: 'Contrat pro', value: 'Contrat pro'},
    {label: 'Stagiaire', value: 'Stagiaire'},
    {label: 'Fonction publique', value: 'Fonction publique'},
    {label: 'Ligne de service', value: 'Ligne de service'},
  ],
  time_modality: [
    {label: 'Temps plein', value: '10'},
    {label: 'Temps partiel', value: '20'},
    {label: "Temps alterné - personnel navigant de l'aéronautique civile", value: '30'},
    {label: 'CPA 2004', value: '40'},
    {label: 'Temps partiel de droit', value: '41'},
    {label: 'Temps partiel de droit pour enfant', value: '42'},
    {label: 'Salarié non concerné', value: '99'},
  ],
  nationality: [
    {label: 'France', value: '01'},
    {label: 'UE', value: '02'},
    {label: 'EEE', value: '03'},
    {label: 'Reste du monde', value: '04'},
  ],
  change: [
    {label: 'Aucun changement', value: 'Aucun changement'},
    {label: 'Promotion', value: 'Promotion'},
    {label: 'Passage en CDI', value: 'Passage en CDI'},
    {label: 'Mutation', value: 'Mutation'},
  ],
  csp: [
    {label: 'Ouvriers', value: 'Ouvriers'},
    {label: 'Employés', value: 'Employés'},
    {label: 'Techniciens', value: 'Techniciens'},
    {label: 'Cadres', value: 'Cadres'},
    {label: 'Profession intermédiaire', value: 'Profession intermédiaire'},
    {label: 'Autres', value: 'Autres'},
  ],
  tranche_age: [
    {label: 'moins de 25 ans', value: 'moins de 25 ans'},
    {label: 'de 25 à 34 ans', value: 'de 25 à 34 ans'},
    {label: 'de 35 à 44 ans', value: 'de 35 à 44 ans'},
    {label: 'de 45 à 54 ans', value: 'de 45 à 54 ans'},
    {label: '55 ans et plus', value: '55 ans et plus'},
  ],
  anciennete: [
    {label: 'moins de 5 ans', value: 'moins de 5 ans'},
    {label: 'de 5 à 10 ans', value: 'de 5 à 10 ans'},
    {label: 'de 10 à 15 ans', value: 'de 10 à 15 ans'},
    {label: 'de 15 à 20 ans', value: 'de 15 à 20 ans'},
    {label: '20 ans et plus', value: '20 ans et plus'},
  ],
  motif_fin: [
    {label: 'Licenciement pour motif économique', value: 'Licenciement pour motif économique'},
    {label: 'Licenciement pour faute', value: 'Licenciement pour faute'},
    {label: 'Licenciement pour inaptitude', value: 'Licenciement pour inaptitude'},
    {label: 'Autres motifs de licenciement', value: 'Autres motifs de licenciement'},
    {label: 'Démission', value: 'Démission'},
    {label: 'Retraite', value: 'Retraite'},
    {label: 'Décès', value: 'Décès'},
    {label: 'Rupture conventionnelle', value: 'Rupture conventionnelle'},
    {label: "Fin de période d'essai", value: "Fin de période d'essai"},
    {label: "Fin de contrat d'apprentissage", value: "Fin de contrat d'apprentissage"},
    {label: 'Fin de CDD', value: 'Fin de contrat à durée déterminée'},
    {label: 'Fin de ligne de service', value: 'Fin de ligne de service'},
    {label: 'Mutation', value: 'Mutation'},
  ],
  motif_arret: [
    {label: 'Maladie non Professionnelle', value: 'Maladie non Professionnelle'},
    {label: 'Maladie profesionnelle', value: 'Maladie profesionnelle'},
    {label: 'Accident du travail', value: 'Accident du travail'},
    {label: 'Accident de trajet', value: 'Accident de trajet'},
    {label: 'Maternité', value: 'Maternité'},
    {label: 'Paternité', value: 'Paternité'},
    {label: 'Adoption', value: 'Adoption'},
    {label: 'Autres', value: 'Autres'},
  ],
  boolean: [
    {label: 'Oui', value: 'Oui'},
    {label: 'Non', value: 'Non'},
  ],
};

export const IndicatorTissotFiltersBooleanValues = {
  hired: ['Oui', 'Non'],
  disabled_worker: ['Oui', 'Non'],
  promoted: ['Oui', 'Non'],
  has_activite: ['Oui', 'Non'],
  present_in_month: ['Oui', 'Non'],
  present_all_month: ['Oui', 'Non'],
  leaving: ['Oui', 'Non'],
  last_contract: ['Oui'],
  present_end_of_month: ['Oui'],
};

export const IndicatorRestitutionValues: Array<{label: string; value: string}> = [
  {label: 'Tableau', value: 'Table'},
  {label: 'Colonnes', value: 'ColumnChart'},
  {label: 'Barres Horizontales', value: 'BarChart'},
  {label: 'Barres', value: 'Bar'},
  {label: 'Aires', value: 'AreaChart'},
  {label: 'Graphique Linéaire', value: 'LineChart'},
  {label: 'Lignes', value: 'Line'},
  {label: 'Camembert', value: 'PieChart'},
];

export const IndicatorPossibleFilters: Array<{label: string; value: string}> = [
  {label: 'Motif de fin de contrat', value: 'motif_fin'},
  {label: "Motif d'arret de travail", value: 'motif_arret'},
];

export interface Filters {
  [key: string]: Array<string>;
}
